import { Line } from '@antv/g2plot'
import dayjs from 'dayjs'
import { useEffect, useMemo, useRef } from 'react'

import { EmptyView } from '@/components/EmptyView.tsx'
import { useThemeMode } from '@/contexts/ThemeContext'
import { getChartThemeColors, getLineChartAxisTheme } from '@/utils/chart-theme'
import { cn } from '@/utils/style'
import { getCssVarRgb } from '@/utils/theme-color'

export interface MonitorItemData {
  name: React.ReactElement
  realtime: React.ReactElement
  values: MonitorValue[]
  isEmpty: boolean
}

interface MonitorValue {
  time: string
  value: number | undefined
  unit: string
}

// Realtime
function getChartRealtime(item: { unit: string; time: string; value: number | string }): string[] {
  return [`${item.value}`]
}

const renderChartRealtime = (item: { unit: string; time: string; value: number | string }) => {
  const descs = getChartRealtime(item)
  return (
    <div className="bg-panel-gray-100 flex items-center py-2 px-3 t-bold gap-3">
      <div className="flex items-center">
        <div className={cn('t-sb', ' text-primary')}>{descs[0]}</div>
        <div className={cn('t-sb', ' text-tertiary')}>{descs[1]}</div>
      </div>
    </div>
  )
}

export const buildChartItemData = (item: any, timePoints: dayjs.Dayjs[]): MonitorItemData => {
  let data: { time: dayjs.Dayjs; value?: number }[]
  let isEmpty = false

  if (!item.matrixArray || item.matrixArray.length === 0) {
    data = timePoints.map((time) => ({
      time,
    }))
    isEmpty = true
  } else {
    data = item.matrixArray.map((v: any) => ({
      time: dayjs(v.matrix[0] * 1000),
      value: v.matrix[1],
    }))
    // 確保按時間排序
    data = data.sort((a, b) => a.time.unix() - b.time.unix())
  }

  const lastData = data.slice(-1)[0]

  return {
    realtime: renderChartRealtime({
      time: lastData?.time?.toString() || '',
      value: lastData?.value || '-',
      unit: item.unit || '',
    }),
    name: item.name,
    values: data.map((v) => ({
      time: v.time.toISOString(),
      value: v.value,
      unit: item.unit || '',
    })),
    isEmpty,
  }
}

export function Chart(props: { value: MonitorItemData }) {
  const { themeMode } = useThemeMode()
  const id = useMemo(() => Math.random().toString(36).replace('0.', 'ID_'), [])
  const lineRef = useRef<Line>()

  useEffect(() => {
    const { values } = props.value
    const unit = values[0]?.unit || ''
    const usage = unit === '%'

    if (values.length > 0) {
      const chartTheme = getChartThemeColors()
      const axisTheme = getLineChartAxisTheme(themeMode, props.value.isEmpty, {
        gridColor: chartTheme.grid,
        showGridInLight: false,
      })
      const chartFill = `l(270) 0:${getCssVarRgb('--color-highlight', '#365FEF', 0)} 1:${getCssVarRgb(
        '--color-highlight',
        '#365FEF',
        0.5,
      )}`

      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
      lineRef.current = new Line(id, {
        data: values,
        padding: 'auto',
        xField: 'time',
        yField: 'value',
        xAxis: {
          tickCount: 7,
          label: {
            formatter: (v) => dayjs(v).format('HH:mm'),
          },
          line: axisTheme.axisLine,
          grid: axisTheme.xGrid,
        },
        yAxis: {
          tickCount: usage ? 6 : undefined,
          label: {
            formatter: (v) => `${v}${unit}`,
          },
          min: usage ? 0 : undefined,
          max: usage ? 100 : undefined,
          grid: axisTheme.yGrid,
        },
        area: {
          style: {
            fill: chartFill,
          },
        },
        tooltip: {
          showMarkers: false,
          formatter: (datum) => ({
            name: datum.name,
            value: datum.value,
            title: dayjs(datum.time).format('YYYY-MM-DD HH:mm:ss'),
          }),
        },
        smooth: true,
        lineStyle: {
          lineWidth: 1,
          stroke: chartTheme.highlight,
        },
      })
      lineRef.current.render()
    }

    return () => {
      if (lineRef.current) {
        lineRef.current.destroy()
        lineRef.current = undefined
      }
    }
  }, [JSON.stringify(props.value), themeMode])

  return (
    <div className="h-[160px] relative">
      <div
        id={id}
        className="h-full w-full overflow-hidden mt-4 text-center text-tertiary t-sm absolute"
      ></div>
      {props.value?.isEmpty && (
        <div className="absolute h-full w-full flex items-center justify-center">
          <EmptyView />
        </div>
      )}
    </div>
  )
}
